/*
 * Bulk savings calculator — order total in, tier discount / savings / total / next-tier hint out.
 * Tiers come from the section's blocks as data-tiers (ascending { min, pct }); see sections/bulk-savings.liquid.
 */

if (!customElements.get('bulk-savings')) {
  customElements.define(
    'bulk-savings',
    class BulkSavings extends HTMLElement {
      connectedCallback() {
        try {
          this.tiers = JSON.parse(this.dataset.tiers || '[]').sort((a, b) => a.min - b.min);
        } catch (error) {
          this.tiers = [];
        }
        if (this.tiers.length === 0) return;

        this.field = this.querySelector('[data-bks-field]');
        this.range = this.querySelector('[data-bks-range]');
        this.pct = this.querySelector('[data-bks-pct]');
        this.save = this.querySelector('[data-bks-save]');
        this.pay = this.querySelector('[data-bks-pay]');
        this.hint = this.querySelector('[data-bks-hint]');
        this.ticks = this.querySelectorAll('[data-bks-tick]');

        const locale = this.dataset.locale || undefined;
        const currency = this.dataset.currency || 'AUD';
        // narrowSymbol: "$" rather than "A$" for an English locale without a region.
        const format = (fractions) => {
          try {
            return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: fractions, maximumFractionDigits: fractions });
          } catch (error) {
            return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'AUD', minimumFractionDigits: fractions, maximumFractionDigits: fractions });
          }
        };
        this.moneyCents = format(2);
        this.moneyRound = format(0);
        // Whole amounts drop the ".00" so the three result figures stay short enough for one line.
        this.money = { format: (value) => (Math.round(value * 100) % 100 === 0 ? this.moneyRound : this.moneyCents).format(value) };

        this.field?.addEventListener('input', () => this.update(this.field.value, 'field'));
        this.range?.addEventListener('input', () => this.update(this.range.value, 'range'));
        this.update(this.field ? this.field.value : 0);
      }

      update(raw, source) {
        const amount = Math.max(0, parseFloat(raw) || 0);

        if (source !== 'field' && this.field) this.field.value = Math.round(amount);
        if (source !== 'range' && this.range) this.range.value = Math.min(amount, Number(this.range.max));
        if (this.range) {
          const fill = (Math.min(amount, Number(this.range.max)) / Number(this.range.max)) * 100;
          this.range.style.setProperty('--bks-fill', `${fill}%`);
        }

        const current = [...this.tiers].reverse().find((tier) => amount >= tier.min);
        const next = this.tiers.find((tier) => amount < tier.min);
        const pct = current ? current.pct : 0;
        const saved = (amount * pct) / 100;

        this.pct.textContent = `${pct}%`;
        this.save.textContent = this.money.format(saved);
        this.pay.textContent = this.money.format(amount - saved);
        this.classList.toggle('is-unlocked', pct > 0);

        this.ticks.forEach((tick) => {
          tick.classList.toggle('is-reached', amount >= Number(tick.dataset.bksTick));
        });

        let message = this.dataset.topLabel || '';
        if (next) {
          const template = (current ? this.dataset.nextLabel : this.dataset.noneLabel) || '';
          message = template
            .replace('[amount]', this.moneyRound.format(Math.ceil(next.min - amount)))
            .replace('[pct]', `${next.pct}%`);
        }
        this.hint.textContent = message;
        this.hint.classList.toggle('is-top', !next);
      }
    }
  );
}
